import type { Coverage, CoverageRatio, CoverageState } from "./types";

/**
 * Client-side mirror of `cloudox/contracts/coverage.py`.
 *
 * The backend serialises the counts and derives the rest as Python properties,
 * which do not cross the wire. These functions reproduce that derivation so a
 * consumer can ask "may I show this number" without reimplementing the rule per
 * component — the version of that mistake this file exists to prevent is the
 * one where a grade appears because a score happened to be non-null.
 *
 * Keep the two in step. Any change here needs the matching change there.
 */

const EMPTY: Coverage = {
  unit: "unit",
  eligible: 0,
  passed: 0,
  failed: 0,
  skipped: 0,
  notApplicable: 0,
  unavailable: 0,
  errored: 0,
  notAssessed: 0,
};

/** A coverage over nothing — the honest default when the field is absent. */
export function emptyCoverage(unit = "unit"): Coverage {
  return { ...EMPTY, unit };
}

/** Eligible units the question applies to. */
export function coverageApplicable(coverage: Coverage): number {
  return coverage.eligible - coverage.notApplicable;
}

/** Applicable units that reached a verdict. */
export function coverageAssessed(coverage: Coverage): number {
  return coverage.passed + coverage.failed;
}

/** Applicable units that owed a verdict and did not produce one. */
export function coverageUnassessed(coverage: Coverage): number {
  return (
    coverage.skipped +
    coverage.unavailable +
    coverage.errored +
    coverage.notAssessed
  );
}

/** Assessed of applicable — the coverage figure itself. */
export function coverageAssessedRatio(coverage: Coverage): CoverageRatio {
  const denominator = coverageApplicable(coverage);
  const numerator = coverageAssessed(coverage);
  return {
    numerator,
    denominator,
    value: denominator === 0 ? null : numerator / denominator,
    basis: `applicable ${coverage.unit}s`,
  };
}

/**
 * Projection onto the shared assessment vocabulary. Monotonic: degrading a
 * unit can never raise this.
 */
export function coverageState(coverage: Coverage): CoverageState {
  const applicable = coverageApplicable(coverage);
  if (coverage.eligible === 0 || applicable === 0) return "not_applicable";
  const assessed = coverageAssessed(coverage);
  if (assessed === 0) {
    return coverage.unavailable + coverage.errored > 0
      ? "unavailable"
      : "not_assessed";
  }
  return assessed < applicable ? "partial" : "assessed_present";
}

/**
 * Whether an aggregate score may be published as a headline.
 *
 * A grade over partial coverage is a claim about work that did not happen. A
 * surface that wants to show the number anyway must show it as provisional,
 * beside {@link describeCoverage}.
 */
export function coveragePermitsHeadline(coverage: Coverage): boolean {
  const state = coverageState(coverage);
  return state === "assessed_present" || state === "assessed_absent";
}

/** One sentence a consumer may render verbatim. */
export function describeCoverage(coverage: Coverage): string {
  const { unit, eligible, notApplicable } = coverage;
  if (eligible === 0)
    return `No ${unit}s were eligible; nothing was evaluated.`;
  const applicable = coverageApplicable(coverage);
  if (applicable === 0) {
    return `All ${eligible} ${unit}s were not applicable to this run; nothing was evaluated.`;
  }
  const parts = [
    `${coverageAssessed(coverage)} of ${applicable} applicable ${unit}s assessed`,
  ];
  if (notApplicable > 0) parts.push(`${notApplicable} not applicable`);
  const rest: readonly (readonly [number, string])[] = [
    [coverage.errored, "errored"],
    [coverage.unavailable, "unavailable"],
    [coverage.skipped, "skipped"],
    [coverage.notAssessed, "never assessed"],
  ];
  for (const [count, label] of rest) {
    if (count > 0) parts.push(`${count} ${label}`);
  }
  return `${parts.join("; ")}.`;
}

/** Normalise the wire `Coverage` object; absent or malformed input is empty. */
export function adaptCoverage(raw: unknown, unit = "unit"): Coverage {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return emptyCoverage(unit);
  }
  const record = raw as Record<string, unknown>;
  const count = (key: string): number => {
    const value = record[key];
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  };
  return {
    unit:
      typeof record.unit === "string" && record.unit.length > 0
        ? record.unit
        : unit,
    eligible: count("eligible"),
    passed: count("passed"),
    failed: count("failed"),
    skipped: count("skipped"),
    notApplicable: count("not_applicable"),
    unavailable: count("unavailable"),
    errored: count("errored"),
    notAssessed: count("not_assessed"),
  };
}
